import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';
import { UploadCloud, CheckCircle, RefreshCw, FileText, Users, UserPlus, Database } from 'lucide-react';
import { DropZone } from '../components/DropZone';
import { DataTable } from '../components/DataTable';
import { TabNav } from '../components/TabNav';
// --- Mock Data for System Matching ---
const EXISTING_LOANS = new Set(['123 main st', '456 oak avenue', '789 elm street', '321 maple drive', '654 pine road']);
const EXISTING_LEADS = new Set(['987 cedar lane', '147 birch blvd', '258 walnut way', '369 spruce court', '741 willow circle']);
// --- Types ---
type ProcessingStatus = 'Matched Loan' | 'Matched Lead' | 'New Lead' | 'Unprocessed';
interface CSVRow {
  [key: string]: string;
}
interface ProcessedRecord extends CSVRow {
  _status: ProcessingStatus;
  _id: string; // unique key for rendering
}
export function CSVUtility() {
  const [activeTab, setActiveTab] = useState('raw');
  const [isProcessing, setIsProcessing] = useState(false);
  const [records, setRecords] = useState<ProcessedRecord[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  // --- Handlers ---
  const handleFileUpload = (file: File) => {
    setIsProcessing(true);
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        const rawData = results.data as CSVRow[];
        const metaFields = results.meta.fields || [];
        // Process data
        const processed = rawData.map((row, index) => {
          // Find street address field (case insensitive search for 'street' or 'address')
          const addressKey = Object.keys(row).find(k => k.toLowerCase().includes('street') || k.toLowerCase().includes('address'));
          const address = addressKey ? row[addressKey]?.toLowerCase().trim() : '';
          let status: ProcessingStatus = 'New Lead';
          if (!address) {
            status = 'Unprocessed'; // No street address found to match against
          } else if (EXISTING_LOANS.has(address)) {
            status = 'Matched Loan';
          } else if (EXISTING_LEADS.has(address)) {
            status = 'Matched Lead';
          }
          return {
            ...row,
            _status: status,
            _id: `row-${index}`
          };
        });
        setHeaders(metaFields);
        setRecords(processed);
        setIsProcessing(false);
        setActiveTab('raw');
      },
      error: error => {
        console.error('Error parsing CSV:', error);
        setIsProcessing(false);
        alert('Error parsing CSV file');
      }
    });
  };
  const handleReset = () => {
    setRecords([]);
    setHeaders([]);
    setFileName('');
    setActiveTab('raw');
  };
  const handleUploadNewLeads = () => {
    alert(`Successfully uploaded ${newLeads.length} new leads to Salesforce!`);
  };
  // --- Derived State ---
  const existingLoans = useMemo(() => records.filter(r => r._status === 'Matched Loan'), [records]);
  const existingLeads = useMemo(() => records.filter(r => r._status === 'Matched Lead'), [records]);
  const newLeads = useMemo(() => records.filter(r => r._status === 'New Lead'), [records]);
  // --- Render Helpers ---
  const getStatusBadge = (status: ProcessingStatus) => {
    switch (status) {
      case 'Matched Loan':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-38">
            Existing Loan
          </span>;
      case 'Matched Lead':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-39">
            Existing Lead
          </span>;
      case 'New Lead':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-40">
            New Lead
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-41">
            Unknown
          </span>;
    }
  };
  const renderContent = () => {
    if (records.length === 0) {
      return <div className="py-12" data-id="element-42">
          <DropZone onFileSelect={handleFileUpload} data-id="element-43" />

          {/* Demo Helper Text */}
          <div className="mt-12 max-w-2xl mx-auto bg-blue-50 border border-blue-100 rounded-lg p-6" data-id="element-44">
            <h4 className="text-sm font-semibold text-blue-900 mb-2" data-id="element-45">
              Demo Instructions
            </h4>
            <p className="text-sm text-blue-700 mb-4" data-id="element-46">
              Upload a CSV with a "Street" or "Address" column. The system will
              match against these hardcoded mock records:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs" data-id="element-47">
              <div data-id="element-48">
                <strong className="block text-blue-900 mb-1" data-id="element-49">
                  Existing Loans (Matches):
                </strong>
                <ul className="list-disc pl-4 text-blue-700 space-y-1" data-id="element-50">
                  {Array.from(EXISTING_LOANS).slice(0, 3).map(e => <li key={e} data-id="element-51">{e}</li>)}
                  <li data-id="element-52">...and more</li>
                </ul>
              </div>
              <div data-id="element-53">
                <strong className="block text-blue-900 mb-1" data-id="element-54">
                  Existing Leads (Matches):
                </strong>
                <ul className="list-disc pl-4 text-blue-700 space-y-1" data-id="element-55">
                  {Array.from(EXISTING_LEADS).slice(0, 3).map(e => <li key={e} data-id="element-56">{e}</li>)}
                  <li data-id="element-57">...and more</li>
                </ul>
              </div>
            </div>
          </div>
        </div>;
    }
    // Prepare columns for display
    // For Raw Data, we add the Status column at the beginning
    const rawColumns = ['Status', ...headers];
    // For other tabs, we just show the CSV data
    const standardColumns = headers;
    let displayData: any[] = [];
    let columns: string[] = [];
    let emptyMsg = '';
    let actionButton = null;
    switch (activeTab) {
      case 'raw':
        displayData = records.map(r => ({
          ...r,
          Status: getStatusBadge(r._status)
        }));
        columns = rawColumns;
        emptyMsg = 'No data available.';
        break;
      case 'loans':
        displayData = existingLoans;
        columns = standardColumns;
        emptyMsg = 'No matches found in Existing Loans.';
        break;
      case 'leads':
        displayData = existingLeads;
        columns = standardColumns;
        emptyMsg = 'No matches found in Existing Leads.';
        break;
      case 'new':
        displayData = newLeads;
        columns = standardColumns;
        emptyMsg = 'No new leads found to upload.';
        actionButton = <button onClick={handleUploadNewLeads} disabled={newLeads.length === 0} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-58">
            <UploadCloud className="w-4 h-4 mr-2" data-id="element-59" />
            Upload {newLeads.length} Leads to Salesforce
          </button>;
        break;
    }
    return <div className="space-y-6" data-id="element-60">
        <div className="flex justify-between items-center" data-id="element-61">
          <div className="flex items-center space-x-4" data-id="element-62">
            <div className="bg-white p-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-63">
              <FileText className="w-5 h-5 text-blue-600" data-id="element-64" />
            </div>
            <div data-id="element-65">
              <h2 className="text-lg font-medium text-slate-900" data-id="element-66">{fileName}</h2>
              <p className="text-sm text-slate-500" data-id="element-67">
                Processed {records.length} rows
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3" data-id="element-68">
            {actionButton}
            <button onClick={handleReset} className="inline-flex items-center px-3 py-2 border border-slate-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-69">
              <RefreshCw className="w-4 h-4 mr-2" data-id="element-70" />
              Start Over
            </button>
          </div>
        </div>

        <DataTable data={displayData} columns={columns} emptyMessage={emptyMsg} data-id="element-71" />
      </div>;
  };
  return <div className="min-h-screen bg-slate-50" data-id="element-72">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10" data-id="element-73">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-74">
          <div className="flex justify-between h-16" data-id="element-75">
            <div className="flex items-center" data-id="element-76">
              <Database className="h-8 w-8 text-blue-600" data-id="element-77" />
              <span className="ml-3 text-xl font-bold text-slate-900 tracking-tight" data-id="element-78">
                DataTriage{' '}
                <span className="text-slate-400 font-normal" data-id="element-79">Utility</span>
              </span>
            </div>
            <div className="flex items-center space-x-4" data-id="element-80">
              <div className="text-sm text-right hidden sm:block" data-id="element-81">
                <p className="font-medium text-slate-900" data-id="element-82">
                  Mortgage Operations
                </p>
                <p className="text-slate-500" data-id="element-83">Salesforce Integration Ready</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold" data-id="element-84">
                JD
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-85">
        <div className="mb-8" data-id="element-86">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-87">
            CSV Import & Triage
          </h1>
          <p className="mt-1 text-slate-500" data-id="element-88">
            Upload lead lists to automatically categorize them against existing
            Salesforce records.
          </p>
        </div>

        {records.length > 0 && <TabNav activeTab={activeTab} onChange={setActiveTab} tabs={[{
        id: 'raw',
        label: 'Raw Data',
        count: records.length
      }, {
        id: 'loans',
        label: 'Existing Loans',
        count: existingLoans.length
      }, {
        id: 'leads',
        label: 'Existing Leads',
        count: existingLeads.length
      }, {
        id: 'new',
        label: 'New Leads (Ready)',
        count: newLeads.length
      }]} data-id="element-89" />}

        {isProcessing ? <div className="flex flex-col items-center justify-center py-24" data-id="element-90">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" data-id="element-91"></div>
            <p className="text-slate-600 font-medium" data-id="element-92">Processing records...</p>
          </div> : renderContent()}
      </main>
    </div>;
}